import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class PerformanceService {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  /**
   * Optimize image URL with WebP support and responsive sizes
   */
  getOptimizedImageUrl(imageUrl: string, width?: number, height?: number, quality: number = 85): string {
    if (!imageUrl) return '';
    
    // Ensure absolute URL
    let fullImageUrl = imageUrl;
    if (!imageUrl.startsWith('http')) {
      fullImageUrl = `https://satel.sa${imageUrl}`;
    }

    // Add optimization parameters for CDN images
    if (fullImageUrl.includes('satel-resources.nyc3.digitaloceanspaces.com')) {
      const separator = fullImageUrl.includes('?') ? '&' : '?';
      const params = [`q=${quality}`, 'f=auto'];
      
      if (width) params.push(`w=${width}`);
      if (height) params.push(`h=${height}`);
      
      return `${fullImageUrl}${separator}${params.join('&')}`;
    }

    return fullImageUrl;
  }

  /**
   * Get responsive image sources for different screen sizes
   */
  getResponsiveImageSources(imageUrl: string, sizes: { [key: string]: { width: number; height?: number } }): string[] {
    return Object.values(sizes).map(size => 
      this.getOptimizedImageUrl(imageUrl, size.width, size.height)
    );
  }

  /**
   * Preload critical resources
   */
  preloadResource(url: string, type: 'image' | 'font' | 'script' | 'style' = 'image'): void {
    if (!this.isBrowser) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = url;
    
    switch (type) {
      case 'image':
        link.as = 'image';
        break;
      case 'font':
        link.as = 'font';
        link.crossOrigin = 'anonymous';
        break;
      case 'script':
        link.as = 'script';
        break;
      case 'style':
        link.as = 'style';
        break;
    }
    
    document.head.appendChild(link);
  }

  /**
   * Lazy load images using Intersection Observer
   */
  lazyLoadImages(): void {
    if (!this.isBrowser) return;

    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset['src']) {
            img.src = img.dataset['src'];
            img.removeAttribute('data-src');
            observer.unobserve(img);
          }
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  }

  /**
   * Debounce function for performance optimization
   */
  debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  /**
   * Throttle function for performance optimization
   */
  throttle<T extends (...args: any[]) => any>(func: T, limit: number): (...args: Parameters<T>) => void {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  /**
   * Check if user is on a slow connection
   */
  isSlowConnection(): boolean {
    if (!this.isBrowser || !('connection' in navigator)) return false;
    
    const connection = (navigator as any).connection;
    return connection.effectiveType === 'slow-2g' || 
           connection.effectiveType === '2g' || 
           connection.effectiveType === '3g';
  }

  /**
   * Optimize based on connection speed
   */
  getOptimizationLevel(): 'high' | 'medium' | 'low' {
    if (!this.isBrowser) return 'medium';
    
    if (this.isSlowConnection()) return 'low';
    
    const connection = (navigator as any).connection;
    if (connection?.effectiveType === '4g') return 'high';
    
    return 'medium';
  }
} 